import moment from 'moment';
import { Item, Order } from 'shared-types';
import { PurchaseParameters } from './analytics.types';

type EventProperties = Record<string, string | number | undefined>;

export function formatPurchaseParams(order: Order, items: Item[]): PurchaseParameters {
  return {
    transaction_id: order.orderReference,
    currency: order.total?.currencyCode ?? '',
    value: formatPrice(order.total?.amount ?? 0),
    items: formatBasketItems(items),
  };
}

export function formatPrice(price: number) {
  return price / 100;
}

export function formatBasketItems(items: Item[]): Gtag.Item[] {
  function getItemId(item: Item) {
    switch (item.type) {
      case 'rooms':
        return item.bookable?.ids?.rateId;
      case 'tables':
        return item.bookable?.ids?.restaurant;
      case 'spa-treatments':
      case 'spa-facilities':
      case 'spa-classes':
        return item.bookable?.ids?.externalId;
      case 'spa-packages':
        return item.spaPackage?.id;
      case 'vouchers':
        return item.voucher?.id;
      case 'events':
        return item.event?.id;
      case 'products':
        return item.product?.id;
    }
  }

  function getItemName(item: Item) {
    switch (item.type) {
      case 'tables':
        return item.bookable?.ids?.restaurantName;
      case 'rooms':
      case 'spa-treatments':
      case 'spa-packages':
      case 'spa-facilities':
      case 'spa-classes':
        return item.bookable?.name;
      case 'vouchers':
        return item.voucher?.name;
      case 'events':
        return item.event?.name;
      case 'products':
        return item.product?.name;
    }
  }

  function getItemVariant(item: Item) {
    switch (item.type) {
      case 'products':
        return item.option?.name;
      case 'vouchers':
        return item.voucherOption?.name;
      case 'events':
        return item.eventOption?.name;
    }
  }

  function getItemSpecificProperties(item: Item): EventProperties | undefined {
    if (item.type === 'rooms') {
      const properties: EventProperties = {
        check_in: item.checkIn,
        check_out: item.checkOut,
        adults: item.adults,
        children: item.children,
      };

      properties.room_count = items.reduce((prev, curr) => {
        if (curr.type === 'rooms') {
          prev += 1;
        }
        return prev;
      }, 0);

      if (item.checkIn && item.checkOut) {
        properties.nights = moment(item.checkOut).diff(item.checkIn, 'days');
      }

      return properties;
    }

    if (item.type?.includes('spa')) {
      const properties: EventProperties = {
        check_in: item.checkIn,
      };
      return properties;
    }
  }

  return items.map((item) => {
    return {
      item_id: getItemId(item),
      item_name: getItemName(item),
      item_category: item.type,
      price: formatPrice(item.price.amount),
      currency: item.price.currencyCode,
      quantity: item.quantity,
      item_variant: getItemVariant(item),
      ...getItemSpecificProperties(item),
    };
  });
}
